import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, ArrowLeft, Mail } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success('Check your email for reset instructions');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center py-12 px-4" data-testid="forgot-password-success">
        <div className="w-full max-w-md text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <Mail className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <h1 className="font-serif text-3xl font-medium mb-2">Check Your Email</h1>
          <p className="text-slate-500 mb-6">
            If an account exists for <strong>{email}</strong>, we've sent password reset instructions.
          </p>
          <p className="text-slate-400 text-sm mb-8">
            Didn't receive an email? Check your spam folder or try again.
          </p>
          <div className="space-y-3">
            <Button 
              onClick={() => setSubmitted(false)}
              variant="outline"
              className="w-full"
            >
              Try Another Email
            </Button>
            <Link to="/login">
              <Button className="w-full btn-primary">
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center py-12 px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Shield className="h-10 w-10" />
          </div>
          <h1 className="font-serif text-3xl font-medium mb-2">Forgot Password?</h1>
          <p className="text-slate-500 text-sm">
            Enter your email and we'll send you a link to reset your password.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="forgot-password-form">
          <div>
            <Label htmlFor="email" className="text-sm font-medium">
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-2 rounded-none border-slate-300 focus:border-slate-900"
              required
              data-testid="forgot-password-email"
            />
          </div>

          <Button 
            type="submit" 
            disabled={loading}
            className="w-full btn-primary"
            data-testid="forgot-password-submit"
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </form>

        {/* Back to Login Link */}
        <Link 
          to="/login" 
          className="flex items-center justify-center mt-8 text-sm text-slate-500 hover:text-slate-900 transition-colors"
          data-testid="back-to-login"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Login
        </Link>
      </div>
    </div>
  );
}
